import React, { useEffect, useState } from "react";
import { createBranch, deleteBranch, getBranches, updateBranch } from "../services/branchService";
import "../styles/Branches.css";

const emptyBranch = { branch_code: "", branch_name: "", state: "", city: "", address: "" };

const BranchesPage = () => {
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(emptyBranch);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  const loadBranches = async () => {
    try {
      setBranches(await getBranches());
      setError("");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to load branches.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranches();
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyBranch);
    setShowForm(true);
    setError("");
  };

  const startEdit = (branch) => {
    setEditingId(branch.id);
    setForm({ branch_code: branch.branch_code || "", branch_name: branch.branch_name || "", state: branch.state || "", city: branch.city || "", address: branch.address || "" });
    setShowForm(true);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleChange = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingId) await updateBranch(editingId, form);
      else await createBranch(form);
      setShowForm(false);
      setEditingId(null);
      setForm(emptyBranch);
      await loadBranches();
    } catch (requestError) {
      const messages = requestError.response?.data?.messages;
      setError(messages?.join(", ") || requestError.response?.data?.message || "Unable to save branch.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (branch) => {
    if (!window.confirm(`Delete branch '${branch.branch_name}'?`)) return;
    setDeletingId(branch.id);
    setError("");
    try {
      await deleteBranch(branch.id);
      await loadBranches();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Unable to delete branch.");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading)
    return (
      <div className="loading loading-with-spinner">
        <span className="spinner" aria-hidden="true" /> Loading branches...
      </div>
    );

  return (
    <div className="branches-page">
      <div className="branches-header">
        <div>
          <p className="eyebrow">Organization</p>
          <h1>Branches</h1>
          <p className="branches-intro">Manage the locations where assets are assigned.</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={startCreate}>
          + New Branch
        </button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {showForm && (
        <form className="branch-editor" onSubmit={handleSubmit}>
          <div className="branch-editor-heading">
            <h2>{editingId ? "Edit branch" : "New branch"}</h2>
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowForm(false)}>
              Cancel
            </button>
          </div>
          <div className="branch-form-grid">
            <div className="form-group">
              <label htmlFor="branch_code">Branch code *</label>
              <input id="branch_code" name="branch_code" value={form.branch_code} onChange={handleChange} maxLength="50" required />
            </div>
            <div className="form-group">
              <label htmlFor="branch_name">Branch name *</label>
              <input id="branch_name" name="branch_name" value={form.branch_name} onChange={handleChange} maxLength="150" required />
            </div>
            <div className="form-group">
              <label htmlFor="state">State *</label>
              <input id="state" name="state" value={form.state} onChange={handleChange} maxLength="100" required />
            </div>
            <div className="form-group">
              <label htmlFor="city">City *</label>
              <input id="city" name="city" value={form.city} onChange={handleChange} maxLength="100" required />
            </div>
            <div className="form-group branch-address">
              <label htmlFor="address">Address *</label>
              <textarea id="address" name="address" value={form.address} onChange={handleChange} maxLength="500" rows="3" required />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update Branch" : "Create Branch"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {branches.length === 0 ? (
        <div className="empty-state">
          <p>No branches found.</p>
        </div>
      ) : (
        <div className="branches-grid">
          {branches.map((branch) => (
            <article className="branch-card" key={branch.id}>
              <div className="branch-card-header">
                <div>
                  <span className="branch-code">{branch.branch_code}</span>
                  <h2>{branch.branch_name}</h2>
                </div>
                <span className="asset-count">{branch.Assets?.length || 0} assets</span>
              </div>
              <dl>
                <div>
                  <dt>State</dt>
                  <dd>{branch.state}</dd>
                </div>
                <div>
                  <dt>City</dt>
                  <dd>{branch.city}</dd>
                </div>
                <div>
                  <dt>Address</dt>
                  <dd>{branch.address}</dd>
                </div>
              </dl>
              <div className="branch-card-actions">
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => startEdit(branch)}>
                  Edit
                </button>
                <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(branch)} disabled={deletingId === branch.id}>
                  {deletingId === branch.id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default BranchesPage;
